import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * FOPR (Fair Opportunity Proposal Request) API Functions
 * Handles document upload, extraction, form submission, and DOCX export
 */

import { extractDetailMessage } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface FOPRDocumentInfo {
  filename: string;
  file_type: string;
  size: number;
  modified_at: string;
}

export interface FOPRDocuments {
  requirement_package?: FOPRDocumentInfo;
  dd2579?: FOPRDocumentInfo;
  sow?: FOPRDocumentInfo;
  performance_time_worksheet?: FOPRDocumentInfo;
  wage_determination?: FOPRDocumentInfo;
  design_specifications?: FOPRDocumentInfo;
  /** site_layout supports multiple files; returned as an array */
  site_layout?: FOPRDocumentInfo[];
  wbs_template?: FOPRDocumentInfo;
  dd1354?: FOPRDocumentInfo;
  submittal_register?: FOPRDocumentInfo;
  daily_inspection_report?: FOPRDocumentInfo;
  rfi_template?: FOPRDocumentInfo;
  base_pass_request?: FOPRDocumentInfo;
  qa_template?: FOPRDocumentInfo;
}

export interface FOPRUploadResponse {
  success: boolean;
  file_path: string;
  file_size: number;
  file_type: string;
}

export interface CLIN {
  number: string;
  prefix: string;
  description: string;
  quantity: number;
  unit: string;
}

export interface Attachment {
  number: number;
  name: string;
  date: string | null;
  page_count: number | null;
  doc_type?: string;
}

export interface WageDetermination {
  number: string;
  classification: string;
  date: string;
}

export interface FOPRData {
  project_title: string;
  qseu_number: string;
  pr_number: string;
  construction_type: string;
  set_aside: string;
  programmed_amount: number;
  magnitude_range: string;
  performance_period_days: number;
  sow_evaluation_paragraph: string;
  has_site_restrictions: boolean;
  wage_determination: WageDetermination | null;
  clins: CLIN[];
  attachments: Attachment[];
  fopr_number: string;
  location: string;
  fopr_issue_date: string;
  proposal_due_datetime: string;
  site_visit_datetime: string;
  questions_due_datetime: string;
  contracting_officer: string;
  contracting_officer_phone: string;
  contract_administrator: string;
  contract_administrator_phone: string;
  project_engineer: string;
  project_engineer_phone: string;
  design_percentages: string;
  /** Badge metadata persisted across flow restores */
  meta_auto_filled_fields?: string[];
  meta_user_edited_fields?: string[];
}

/**
 * Get user email from localStorage for authentication headers
 */
function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Upload a document for FOPR (stores on backend)
 * @param flowId - The flow/session identifier
 * @param docType - Type of document (e.g. "requirement_package", "dd2579")
 * @param file - The file to upload
 * @returns Upload response with file path and size
 */
export async function uploadFOPRDocument(
  flowId: string,
  docType: string,
  file: File
): Promise<FOPRUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('flow_id', flowId);
  formData.append('doc_type', docType);

  const userEmail = getUserEmail();
  const headers: Record<string, string> = {};
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/upload-document`, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    const detail = extractDetailMessage(error.detail, `Upload failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Get list of uploaded FOPR documents for a flow
 * @param flowId - The flow/session identifier
 * @returns Object containing info about uploaded documents
 */
export async function getFOPRDocuments(
  flowId: string
): Promise<Record<string, { filename: string; size: number; content_type: string }>> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/${flowId}/documents`, {
    credentials: 'include',
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get documents' }));
    const detail = extractDetailMessage(error.detail, `Failed to get documents with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Delete a specific FOPR document
 * @param flowId - The flow/session identifier
 * @param docType - Type of document to delete
 */
export async function deleteFOPRDocument(
  flowId: string,
  docType: string
): Promise<{ success: boolean }> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/${flowId}/documents/${docType}`, {
    credentials: 'include',
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    const detail = extractDetailMessage(error.detail, `Delete failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Delete a specific site layout file by filename
 * @param flowId - The flow/session identifier
 * @param filename - The filename to delete (will be URL-encoded)
 */
export async function deleteFOPRSiteLayoutFile(
  flowId: string,
  filename: string
): Promise<void> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const encodedFilename = encodeURIComponent(filename);
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/fopr/${flowId}/documents/site_layout/${encodedFilename}`,
    {
      credentials: 'include',
    method: 'DELETE',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    const detail = extractDetailMessage(error.detail, `Delete failed with status ${response.status}`);
    throw new Error(detail);
  }
}

/**
 * Run FOPR extraction on uploaded documents
 * @param flowId - The flow/session identifier
 * @returns Extracted FOPRData
 */
export async function runFOPRExtraction(flowId: string, forceRefresh = false): Promise<FOPRData> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/run`, {
    credentials: 'include',
    method: 'POST',
    headers,
    body: JSON.stringify({ flow_id: flowId, force_refresh: forceRefresh }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to run extraction' }));
    const detail = extractDetailMessage(error.detail, `Failed to run extraction with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Submit the completed FOPR form
 * @param flowId - The flow/session identifier
 * @param formData - The completed form data
 */
export async function submitFOPRForm(
  flowId: string,
  formData: Partial<FOPRData>
): Promise<{ success: boolean }> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/${flowId}/form`, {
    credentials: 'include',
    method: 'PUT',
    headers,
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to submit form' }));
    const detail = extractDetailMessage(error.detail, `Failed to submit form with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Export the FOPR as a DOCX file (triggers browser download)
 * @param flowId - The flow/session identifier
 */
export async function exportFOPRDocx(flowId: string): Promise<void> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {};
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/${flowId}/export-docx`, {
    credentials: 'include',
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to export DOCX' }));
    const detail = extractDetailMessage(error.detail, `Failed to export DOCX with status ${response.status}`);
    throw new Error(detail);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `FOPR_${flowId}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Get saved FOPR extraction data for a flow (stage 501)
 * @param flowId - The flow/session identifier
 * @returns Saved FOPRData if extraction has been run, throws on 404 if not
 */
export async function getFOPRData(flowId: string): Promise<FOPRData> {
  const userEmail = getUserEmail();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/fopr/${flowId}/data`, {
    credentials: 'include',
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get FOPR data' }));
    const detail = extractDetailMessage(error.detail, `Failed to get FOPR data with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}
